/** * ステイタス詳細表示コンポーネント */
<template>
  <ifield-b-modal
    id="the-status-detail"
    ref="the-status-detail-modal"
    size="xl"
    centered
    no-body-border
    v-bind:title="$t('theStatusDetail.status')"
    static
    modal-class="detail-dialog-static"
    v-on:show="$_onShowModal"
    v-on:hidden="$_onHideModal"
  >
    <b-row v-if="isContentCreated" class="dialog-body">
      <b-col cols="6" class="dialog-body-left">
        <loading-mask v-bind:isLoading="isLoading">
          <!-- ユーザー -->
          <div class="dialog-user-card">
            <card-user
              v-if="data"
              can-user-name-click
              v-bind:user-id="data.user_id"
              v-bind:user-name="data.user_name"
              v-bind:profile-image-attach-file-type="
                data.profile_image ? data.profile_image.attach_file_type : null
              "
              v-bind:groups="groups"
              v-on:usercard-username-click="
                $store.dispatch('updateProfileUserId', $event)
              "
            ></card-user>
          </div>
          <div class="dialog-info-card">
            <!-- データリスト -->
            <div>
              <data-list v-bind:data="detailDataList" />
            </div>
            <!-- ステイタス基本情報 -->
            <div class="dialog-info-title">
              <p class="title">
                {{ statusName }}
              </p>
            </div>
            <p class="dialog-info-comment">
              {{ statusComment }}
            </p>
          </div>
        </loading-mask>
      </b-col>
      <b-col cols="6" class="dialog-body-right">
        <!-- 地図 -->
        <map-control
          ref="map-control"
          v-bind="$store.getters.mapControlBinds"
          v-bind:id-prefix="mapIdPrefix"
          v-bind:height="'520px'"
          v-bind:center="center"
          v-bind:zoom="18"
          v-bind:store-sync="['mapType']"
          v-bind:control="{ header: false, layerMenu: false }"
          v-bind:can-change-indoor-level="false"
          v-bind:layer-renderers="renderers"
          v-bind:reactive-render="isModalShown"
          v-on:map-control-initend="$_onMapInitEnd"
        />
      </b-col>
    </b-row>

    <!-- modal footer -->
    <template v-slot:modal-footer="{ cancel }">
      <!-- 閉じるボタン -->
      <b-button
        size="sm"
        variant="secondary"
        v-t="'theStatusDetail.action.close'"
        v-on:click="cancel"
      />
    </template>
  </ifield-b-modal>
</template>

<script>
import DataList from '@/components/AppDataList.vue'
import CardUser from '@/components/AppCardUser.vue'
import { RestApiClient } from 'ifield-core-components'
import { mapState } from 'vuex'
import DomainRenderer from '@/js/map-renderer/DomainRenderer.js'
import { toLocalTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

/**
 * AppDataListに設定するデータタイプ
 */
const DATA_LIST_TYPE = {
  DATE_TIME: 5,
}

// レイヤーID
const LAYER_ID = 'status'

// レンダラーファクトリ
const rendererFactory = new DomainRenderer({ layerId: LAYER_ID })

export default {
  name: 'TheStatusDetail',
  components: {
    'data-list': DataList,
    'card-user': CardUser,
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      /**
       * 表示対象のステイタスID
       * @type {number}
       */
      statusId: null,
      /**
       * ステイタス詳細データ
       *
       * API(/ifield/users/api/status/:id)のレスポンス
       * @type {Object}
       */
      data: null,
      /**
       * ロード中の場合はtrue
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * 自レイヤーのレンダラー
       * @type {Object}
       */
      myRenderer: null,
      /**
       * ダイアログの中身を生成済みの場合はtrue
       *
       * 地図を作り直さないため、一度開いたら閉じても保持する
       * @type {Boolean}
       */
      isContentCreated: false,
      /**
       * ダイアログを表示中の場合はtrue
       *
       * 非表示の間は地図のリアクティブな描画を止める
       * @type {Boolean}
       */
      isModalShown: false,
    }
  },
  computed: {
    /**
     * 地図要素のIDプレフィックス
     *
     * 同じ画面に本コンポーネントが複数載るため、インスタンスごとに一意にする
     * @type {String}
     */
    mapIdPrefix: function () {
      return `status-detail-${this._uid}-`
    },
    ...mapState('userMarker', ['iconAtlas', 'iconMapping']),
    /**
     * 報告ユーザーの所属グループ
     * @type {Array.<Object>}
     */
    groups: function () {
      if (!this.data) return []

      return (
        this.$store.getters['user/getUserById'](this.data.user_id)?.groups || []
      )
    },
    /**
     * 地図binding用render関数配列
     * @type {Array.<Function>}
     */
    renderers: function () {
      const renderers = [...this.$store.getters['map/layerRenderers']]
      if (this.myRenderer) {
        renderers.push(this.myRenderer.render)
      }
      return renderers
    },
    /**
     * 中心座標
     * @type {Object}
     */
    center: function () {
      if (
        this.data &&
        this.data.geom &&
        this.data.geom.geometry &&
        this.data.geom.geometry.coordinates
      ) {
        const [lng, lat] = this.data.geom.geometry.coordinates
        return { lat, lng }
      } else {
        return this.$store.getters['map/center']
      }
    },
    /**
     * @return {string} ステイタス名
     */
    statusName: function () {
      if (this.data) {
        return this.data.status_name
      } else {
        return ''
      }
    },

    /**
     * @return {string} ステイタスのコメント
     */
    statusComment: function () {
      if (this.data) {
        return this.data.comment
      } else {
        return ''
      }
    },

    /**
     * ステイタス詳細データからデータリストコンポーネント用のデータを作成する
     */
    detailDataList: function () {
      if (!this.data) {
        return null
      }

      // 標準項目
      var standard = [
        // 送信日時
        {
          label: this.$t('theStatusDetail.field.sendDatetime'),
          type: DATA_LIST_TYPE.DATE_TIME,
          value: this.data.status_update_at,
        },
        // 送信位置
        {
          label: this.$t('theStatusDetail.field.sendLocation'),
          value: this.$_formatGeom(this.data.geom),
        },
      ]

      return {
        standard: standard,
      }
    },
  },
  watch: {
    data: function () {
      this.$_updatePinImage()
        .then(() => this.$store.dispatch('userMarker/updateTexture'))
        .then(() => this.$_updateStatusLayer())
    },
  },
  mounted: function () {
    this.$log.info('statusDetail mounted')
  },
  methods: {
    /**
     * モーダルダイアログを表示する
     * @param {number} statusId 対象のステイタスID
     */
    showModal: function (statusId) {
      this.statusId = statusId
      this.$_showStatus()
      this.$refs['the-status-detail-modal'].show()
    },

    /**
     * モーダルダイアログを非表示にする
     */
    hideModal: function () {
      this.$refs['the-status-detail-modal'].hide()
    },

    /**
     * マップ初期化完了時処理
     */
    $_onMapInitEnd: function () {
      this.map = this.$refs['map-control']
    },

    /**
     * ステイタス情報を表示する
     */
    $_showStatus: async function () {
      if (!this.statusId) {
        return
      }

      this.$_showLoading()
      // URL作成
      var url = RestApiClient.createApiUrl('status/' + this.statusId)
      try {
        const d = await RestApiClient.executeGetApi(url).then(
          ({ data }) => data
        )
        this.data = {
          ...d,
          status_update_at: toLocalTz(d.status_update_at),
        }
      } catch (error) {
        this.handleApiError(error)
        this.$_hideLoading()
        return
      }

      this.$_hideLoading()
    },

    /**
     * 地図上に表示するピン画像データを更新
     */
    $_updatePinImage: async function () {
      if (!this.data) return

      const id = this.data.user_id
      try {
        await this.$store.dispatch('userMarker/getImageById', { id })
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn(`failed to update pin image of user ID ${id}.`)
        )
      }
    },

    /**
     * ステイタスレイヤー更新
     */
    $_updateStatusLayer: function () {
      if (!this.data) {
        this.myRenderer = null
        return
      }

      const props = {
        iconAtlas: this.iconAtlas,
        iconMapping: this.iconMapping,
        getIcon: (d) => {
          if (this.iconMapping[`${d.properties.user_id}`]) {
            return `${d.properties.user_id}`
          }
          return 'default'
        },
        updateTriggers: {
          getIcon: [this.iconMapping],
        },
      }
      this.myRenderer = rendererFactory.createRenderer([this.data], props)
    },

    /**
     * 位置情報を表示用にフォーマットする
     */
    $_formatGeom: function (geom) {
      const { type, coordinates } = geom.geometry
      if (type === 'Point') {
        return coordinates[1] + ', ' + coordinates[0]
      } else if (type === 'LineString') {
        // Point以外は、とりあえず最初の１個目だけ返す
        return coordinates[0][1] + ', ' + coordinates[0][0]
      } else {
        return coordinates[0][0][1] + ', ' + coordinates[0][0][0]
      }
    },

    /**
     * モーダルダイアログを表示する時の処理
     */
    $_onShowModal: function () {
      // 地図を作り直さないよう、一度生成した中身は閉じても保持する
      this.isContentCreated = true
      this.isModalShown = true
    },

    /**
     * モーダルダイアログ非表示時の処理
     */
    $_onHideModal: function () {
      this.isModalShown = false
      this.statusId = null
      this.data = null
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-body {
  background-color: #ffffff;
  display: flex;
  height: 520px;
  margin: 0;
  width: 100%;
}

.dialog-body-left {
  padding-left: 0;
  height: 100%;
}

.dialog-user-card {
  width: 100%;
  height: auto;
  padding: 10px;
  box-sizing: border-box;
  background-color: #ffffff;
  border: 1px solid #f0f0f0;
}

.dialog-info-card {
  background-color: #f0f0f0;
  border: 1px solid #f0f0f0;
  height: calc(100% - 62px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 20px;
  @include mixin-scrollbar;

  ::v-deep .dialog-info-card {
    padding: 0;
    overflow-y: hidden;
  }
}

.dialog-info-title {
  background: #d9dcdf;
  margin: 10px 10px 10px 0;
  padding: 10px 0 10px 0;
  text-align: center;
  width: 100%;

  .title {
    font-size: 1.2em;
    font-weight: $font-weight-bold;
    margin-bottom: 0;
  }
}

.dialog-info-comment {
  font-size: $font-size-small;
  margin: 20px 0;
  text-align: left;
}

.dialog-body-right {
  padding-right: 0;
}
</style>
